import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import useAdminDialog from '../components/AdminDialog';
import { useAdminLanguage } from '../AdminLanguageContext';

const yuan = (cents) => (Number(cents || 0) / 100).toFixed(2);
const FILTER_VALUES = new Set(['all', 'pending', 'verifying', 'processing', 'paid', 'completed', 'cancelled']);

const STATUS_BADGE = {
  pending: 'badge-orange',
  verifying: 'badge-orange',
  processing: 'badge-blue',
  paid: 'badge-green',
  completed: 'badge-green',
  cancelled: 'badge-gray',
};

function proofAssetsFor(item) {
  if (Array.isArray(item.payment_proof_assets)) return item.payment_proof_assets;
  if (!item.payment_proof_assets) return [];
  try {
    const parsed = JSON.parse(item.payment_proof_assets);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function CheckoutRequests() {
  const [searchParams] = useSearchParams();
  const { copy } = useAdminLanguage();
  const checkoutRequestsCopy = copy.checkoutRequests;
  const initialStatus = FILTER_VALUES.has(searchParams.get('status')) ? searchParams.get('status') : 'verifying';
  const STATUS_LABEL = {
    pending: checkoutRequestsCopy.status.pending,
    verifying: checkoutRequestsCopy.status.verifying,
    processing: checkoutRequestsCopy.status.processing,
    paid: checkoutRequestsCopy.status.paid,
    completed: checkoutRequestsCopy.status.completed,
    cancelled: checkoutRequestsCopy.status.cancelled,
  };
  const STATUS_OPTIONS = [
    { value: 'all', label: checkoutRequestsCopy.filters.allStatus },
    { value: 'pending', label: checkoutRequestsCopy.status.pending },
    { value: 'verifying', label: checkoutRequestsCopy.status.verifying },
    { value: 'processing', label: checkoutRequestsCopy.status.processing },
    { value: 'paid', label: checkoutRequestsCopy.status.paid },
    { value: 'completed', label: checkoutRequestsCopy.status.completed },
    { value: 'cancelled', label: checkoutRequestsCopy.status.cancelled },
  ];
  const [status, setStatus] = useState(initialStatus);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { confirmAction, requestText, dialog } = useAdminDialog();

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.get('/admin/finance/checkout-requests', { params: { status } })
      .then(res => setRequests(res.data.requests || []))
      .catch(err => setError(err.response?.data?.error || checkoutRequestsCopy.errors.loadFailed))
      .finally(() => setLoading(false));
  }, [status, checkoutRequestsCopy.errors.loadFailed]);

  useEffect(() => { load(); }, [load]);

  const totalAmount = useMemo(
    () => requests.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [requests]
  );

  const updateStatus = async (item, nextStatus) => {
    if (item.status === nextStatus) return;
    if (!await confirmAction({
      title: checkoutRequestsCopy.confirm.title,
      message: checkoutRequestsCopy.confirm.message(item.title, STATUS_LABEL[nextStatus] || nextStatus),
      variant: nextStatus === 'cancelled' ? 'danger' : 'primary',
    })) return;
    await api.post(`/admin/finance/checkout-requests/${item.id}/status`, { status: nextStatus });
    load();
  };

  const confirmPaid = async (item) => {
    if (item.status === 'paid') return;
    const adminNote = await requestText({
      title: checkoutRequestsCopy.confirmPaid.title,
      message: checkoutRequestsCopy.confirmPaid.message(item.title),
      placeholder: checkoutRequestsCopy.confirmPaid.placeholder,
      defaultValue: item.admin_note || '',
      confirmText: checkoutRequestsCopy.actions.confirmPaid,
    });
    if (adminNote === null) return;
    await api.post(`/admin/finance/checkout-requests/${item.id}/status`, {
      status: 'paid',
      adminNote,
    });
    load();
  };

  const downloadProof = async (item, asset) => {
    if (!asset?.assetId) return;
    setError('');
    try {
      const res = await api.get(`/admin/finance/checkout-requests/${item.id}/proof-assets/${asset.assetId}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noreferrer';
      link.download = asset.name || `${checkoutRequestsCopy.columns.paymentProof}-${asset.assetId}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      setError(err.response?.data?.error || checkoutRequestsCopy.errors.loadFailed);
    }
  };

  return (
    <div>
      {dialog}
      <div className="page-title">{checkoutRequestsCopy.title}</div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {STATUS_OPTIONS.map(option => (
            <button
              key={option.value}
              className={`btn btn-sm ${status === option.value ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setStatus(option.value)}
            >
              {option.label}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            {checkoutRequestsCopy.summary(requests.length, yuan(totalAmount))}
          </span>
        </div>
      </div>

      {error && (
        <div className="card" style={{ color: '#b91c1c', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{checkoutRequestsCopy.columns.id}</th>
                <th>{checkoutRequestsCopy.columns.applicant}</th>
                <th>{checkoutRequestsCopy.columns.project}</th>
                <th>{checkoutRequestsCopy.columns.amount}</th>
                <th>{checkoutRequestsCopy.columns.description}</th>
                <th>{checkoutRequestsCopy.columns.paymentInfo}</th>
                <th>{checkoutRequestsCopy.columns.paymentProof}</th>
                <th>{checkoutRequestsCopy.columns.status}</th>
                <th>{checkoutRequestsCopy.columns.createdAt}</th>
                <th>{checkoutRequestsCopy.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                    {checkoutRequestsCopy.loading}
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                    {checkoutRequestsCopy.empty}
                  </td>
                </tr>
              ) : requests.map(item => {
                const proofAssets = proofAssetsFor(item);
                return (
                  <tr key={item.id}>
                    <td style={{ color: '#9ca3af' }}>#{item.id}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{item.username || checkoutRequestsCopy.emptyValue}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.email || ''}</div>
                    </td>
                    <td style={{ fontWeight: 600, maxWidth: 220 }}>{item.title}</td>
                    <td style={{ color: '#1dbf73', fontWeight: 700 }}>¥ {yuan(item.amount)}</td>
                    <td style={{ maxWidth: 240, color: '#6b7280' }}>{item.description || checkoutRequestsCopy.emptyValue}</td>
                    <td style={{ minWidth: 180, maxWidth: 260, color: '#4b5563', fontSize: 12, lineHeight: 1.7 }}>
                      <div>{checkoutRequestsCopy.labels.method}: {item.payment_method || checkoutRequestsCopy.emptyValue}</div>
                      {item.payment_reference && <div>{checkoutRequestsCopy.labels.reference}: {item.payment_reference}</div>}
                      {item.payer_name && <div>{checkoutRequestsCopy.labels.payer}: {item.payer_name}</div>}
                      {item.payment_time && <div>{checkoutRequestsCopy.labels.paymentTime}: {String(item.payment_time).replace('T', ' ')}</div>}
                      {item.proof_submitted_at && <div>{checkoutRequestsCopy.labels.submittedAt}: {String(item.proof_submitted_at).slice(0, 16)}</div>}
                      {item.verified_at && <div>{checkoutRequestsCopy.labels.verifiedAt}: {String(item.verified_at).slice(0, 16)}</div>}
                      {item.admin_note && <div>{checkoutRequestsCopy.labels.adminNote}: {item.admin_note}</div>}
                    </td>
                    <td style={{ minWidth: 140 }}>
                      {proofAssets.length ? proofAssets.map((asset, index) => (
                        <button
                          key={asset.assetId || asset.url || index}
                          className="btn btn-sm btn-outline"
                          type="button"
                          onClick={() => downloadProof(item, asset)}
                          style={{ margin: '0 6px 6px 0' }}
                        >
                          {asset.name || `${checkoutRequestsCopy.columns.paymentProof} ${index + 1}`}
                        </button>
                      )) : checkoutRequestsCopy.emptyValue}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[item.status] || 'badge-gray'}`}>
                        {STATUS_LABEL[item.status] || item.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                      {item.created_at ? String(item.created_at).slice(0, 16) : checkoutRequestsCopy.emptyValue}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {item.status === 'verifying' && (
                          <button className="btn btn-sm btn-primary" onClick={() => confirmPaid(item)}>
                            {checkoutRequestsCopy.actions.confirmPaid}
                          </button>
                        )}
                        {item.status === 'paid' && (
                          <button className="btn btn-sm btn-outline" onClick={() => updateStatus(item, 'processing')}>
                            {checkoutRequestsCopy.actions.markProcessing}
                          </button>
                        )}
                        {['paid', 'processing'].includes(item.status) && (
                          <button className="btn btn-sm btn-outline" onClick={() => updateStatus(item, 'completed')}>
                            {checkoutRequestsCopy.actions.complete}
                          </button>
                        )}
                        {item.status !== 'cancelled' && (
                          <button
                            className="btn btn-sm btn-outline"
                            style={{ color: '#ef4444', borderColor: '#fecaca' }}
                            onClick={() => updateStatus(item, 'cancelled')}
                          >
                            {checkoutRequestsCopy.actions.cancel}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
